import { orderBy } from "lodash";

import type { ILenderResponse, IResolvedBid } from "../types/lenderResponse";

export type SortKey =
  // == works when there are multiple bids ==
  | "bestOffer"
  | "totalLoanAmount"
  // == Only works for single bid lender responses ==
  | "amount"
  | "paybackTimeMonths"
  | "monthlyCost"
  | "firstMonthInterestRate"
  | "comparableMonthlyInterestRate"
  | "comparableYearlyInterestRate"
  | "limit"
  | "averageMonthlyPayment"
  | "commissionPercent"
  | "withRegress";

export interface ISortBidsBy {
  field: SortKey;
  order: "asc" | "desc";
}

const HIGH_RATE_FORECAST_THRESHOLD = 3.4;

const getBidFieldSortValue = (lr: ILenderResponse, field: SortKey): number => {
  if (field === "totalLoanAmount") {
    return lr.totalLoanAmount ?? 0;
  }

  const firstBid = lr.bids[0];
  if (!firstBid) return 0;

  if (field === "withRegress") {
    if (
      "withRegress" in firstBid &&
      typeof firstBid.withRegress === "boolean"
    ) {
      return firstBid.withRegress ? 1 : 0;
    }
    return 0;
  }

  if (field in firstBid) {
    const val = firstBid[field as keyof IResolvedBid];
    const n = Number(val);
    return Number.isFinite(n) ? n : 0;
  }

  return 0;
};

const sortBids = (
  lenderResponses: ILenderResponse[],
  sortBy: ISortBidsBy,
  forecastBidMinRate?: number | null,
) => {
  const useHighRateForecast =
    forecastBidMinRate != null &&
    forecastBidMinRate >= HIGH_RATE_FORECAST_THRESHOLD;

  return orderBy<ILenderResponse>(
    lenderResponses,
    sortBy.field === "bestOffer"
      ? useHighRateForecast
        ? [
            // high_rate_forecast_predicate: attractiveness desc → firstMonthInterestRate asc → createdAt asc
            (lr) => lr.attractivenessScore ?? 0,
            (lr) =>
              lr.bids.length > 0
                ? lr.bids.reduce(
                    (acc, bid) => acc + bid.firstMonthInterestRate,
                    0,
                  ) / lr.bids.length
                : 0,
            (lr) => lr.createdAt,
          ]
        : [
            // normal predicate: attractivenessScore desc → totalLoanAmount desc → comparableMonthlyInterestRate asc → createdAt asc
            (lr) => lr.attractivenessScore ?? 0,
            (lr) => lr.totalLoanAmount,
            (lr) =>
              lr.bids.reduce(
                (acc, bid) => acc + bid.comparableMonthlyInterestRate,
                0,
              ) / lr.bids.length,
            (lr) => lr.createdAt,
          ]
      : [
          (lr) => {
            try {
              return getBidFieldSortValue(lr, sortBy.field);
            } catch (_e) {
              const firstBid = lr.bids[0];
              if (!firstBid) return 0;
              return firstBid[sortBy.field as keyof IResolvedBid];
            }
          },
          (lr) => lr.createdAt,
        ],
    sortBy.field === "bestOffer"
      ? useHighRateForecast
        ? ["desc", "asc", "asc"]
        : ["desc", "desc", "asc", "asc"]
      : [sortBy.order, "asc"],
  );
};

export { sortBids };
